import Button from '@/components/Button';
import { Coupon } from '@/components/Coupon';
import { CouponGroup } from '@/components/CouponGroup';
import { GameKenoTypeInfo } from '@/components/GameKenoTypeInfo';
import { GameProps } from '@/components/Gameinterfaces';
import { Icon, IconVariant } from '@/components/Icon';
import Modal, { ModalContent, ModalHeader } from '@/components/Modal';
import ModalQuickTipsCouponAmount from '@/components/ModalQuickTipCouponAmount';
import ModalQuickTipDrawTypes from '@/components/ModalQuickTipDrawTypes';
import { QuickTips } from '@/components/QuickTips';
import StepTransition, { TransitionVariant } from '@/components/StepTransition';
import { SummaryPrice } from '@/components/SummaryPrice';
import Table from '@/components/Table';
import { WeeksNumberSelection } from '@/components/WeeksNumberSelection';
import { kenoData } from '@/config/odds/oddsConfigs';
import MarketingParams from '@/helpers/MarketingParams';
import { calculateCouponProgress } from '@/helpers/calculateCouponProgress';
import { countFilledCoupons } from '@/helpers/countFilledCoupons';
import { updateCouponNumbers } from '@/helpers/updateCouponNumbers';
import classnames from '@/utilities/classnames';
import createArrayFromNumber from '@/utilities/createArrayFromNumber';
import getRandomNumberArray from '@/utilities/getRandomNumberArray';
import numberToCurrency from '@/utilities/numberFormatter';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import replaceAbbreviations from '@/utilities/replaceAbbreviations';
import { findSubsidiaryById, subsidiaryConfig } from '@/utilities/subsidiaries';
import React from 'react';

interface GameStepCouponsProps extends GameProps {
  [key: string]: any;
}

export enum QuickTipType {
  Zodiac = 'Zodiac',
  Random = 'Random',
  Stats = 'Stats',
  Game = 'Game',
}

interface GameStepCouponsState {
  currentQuickTip: QuickTipType;
  stateManuallyChanged: boolean;
}

class GameStepCoupons extends React.Component<
  GameStepCouponsProps,
  GameStepCouponsState
> {
  currentSubsidiary: string;
  currentMaxCrossedAmount: number;

  constructor(props: GameStepCouponsProps) {
    super(props);

    this.state = {
      currentQuickTip: QuickTipType.Random,
      stateManuallyChanged: false,
    };
    this.currentSubsidiary = '';
  }

  replaceCouponModalInfoSpaceholders = (
    text: string,
    subsidiary,
    amountCoupons
  ): { __html: string } => {
    const subText = subsidiary
      ? subsidiary === 'Saarland'
        ? `im ${subsidiary}`
        : `in ${subsidiary}`
      : '';
    // Replace placeholders with actual values
    const replacedText = text
      .replace(/%%currentSubsidiary%%/g, subText)
      .replace(/%%amountCoupons%%/g, amountCoupons);
    return replaceAbbreviations(replacedText);
  };

  getMaxCoupons = (id: string) => {
    const currentSub = subsidiaryConfig.find((current) => current.id === id);
    if (currentSub) {
      return currentSub.couponAmounts[this.props.id];
    }
    return this.props.couponGroup?.initialAmountCoupons || 0;
  };

  componentDidMount = () => {
    // Bereits vor und zurück gegangen (props)? Oder LocalStorage vorhanden?
    const currentSubsidiary = this.props.subsidiary
      ? this.props.subsidiary.substring(0, 2)
      : typeof window !== 'undefined'
        ? localStorage?.getItem('currentSubsidiary')
        : this.currentSubsidiary;

    if (currentSubsidiary && currentSubsidiary !== this.props.subsidiary) {
      this.currentSubsidiary = currentSubsidiary;
      this.props.onSubsidiaryUpdate(currentSubsidiary);
    }

    if (this.props.couponGroup) {
      this.props.couponGroup.amountCoupons = this.props.couponGroup
        .initialAmountCoupons
        ? currentSubsidiary
          ? this.getMaxCoupons(currentSubsidiary)
          : this.props.couponGroup.initialAmountCoupons
        : 0;
    }
  };

  setFocusOnModalContent = (): void => {
    const modalContent = document.querySelector('.Modal__header-close');
    if (modalContent) {
      (modalContent as HTMLElement).focus();
    }
  };
  setFocusOnOpenerButton = (): void => {
    const modalOpener = document.querySelector('#cuponModalOpener');
    if (modalOpener) {
      setTimeout(() => (modalOpener as HTMLElement).focus(), 100);
    }
  };

  handleOnCouponClick = (couponIndex: number) => {
    this.props.onCurrentCouponIdUpdate(this.props.id, couponIndex);
  };

  handleOnForwardClick = () => {
    this.props.onCurrentCouponIdUpdate(
      this.props.id,
      this.props.currentCouponId + 1
    );
  };

  handleOnCouponNumberClick = (coupon, group, value) => {
    this.props.onCouponNumberClick(this.props.id, coupon, group, value);
  };

  handleOnCouponClear = (coupon) => {
    if (this.props.couponGroup) {
      updateCouponNumbers(coupon, this.props, []);
    }
  };

  handleOnCouponClearAll = () => {
    this.props.onResetGameState(this.props.id.toString());
  };

  handleOnCouponFill = (coupon: number, count?: number) => {
    updateCouponNumbers(coupon, this.props, (group, i, excluded) => {
      const size = count || this.currentMaxCrossedAmount || group.maxCrossed;
      const min = group.startValue;
      const max = group.amountNumbers;
      const unique = true;

      return getRandomNumberArray(size, min, max, unique, excluded);
    });
  };

  handleSubsidiaryChange = (sub, couponGroup) => {
    if (couponGroup) {
      this.setState({
        stateManuallyChanged: true,
      });
      // URL-Parameter löschen wg. zurück-Button
      window?.history.pushState({}, document?.title, window?.location.pathname);

      // default
      let maxCoupons = (couponGroup.amountCoupons = couponGroup
        ? couponGroup.initialAmountCoupons
        : 0);
      if (sub === '') {
        this.currentSubsidiary = '';
        couponGroup.amountCoupons = maxCoupons;
        this.props.onSubsidiaryUpdate('');
        localStorage?.setItem('currentSubsidiary', '');
      } else {
        const currentSub = findSubsidiaryById(sub);
        if (currentSub) {
          const currentGameId = this.props.id;
          const amounts = currentSub.couponAmounts[currentGameId];
          if (amounts) {
            this.currentSubsidiary = currentSub.id;
            this.props.onSubsidiaryUpdate(currentSub.id);
            maxCoupons = amounts;
            localStorage?.setItem('currentSubsidiary', currentSub.id);
            couponGroup.amountCoupons = maxCoupons;
          }
        }
      }
      // Preisupdate
      const filledCoupons = countFilledCoupons(
        this.props.crossedNumbers,
        couponGroup.numberGroups
      );
      if (filledCoupons > maxCoupons) {
        for (let index = maxCoupons + 1; index <= filledCoupons; index++) {
          this.handleOnCouponClear(index);
        }
      }
    }
  };

  handleOnCouponFillAll = (
    amount: number,
    amountCouponNumbers?: number
  ): void => {
    if (!this.props.couponGroup) {
      return;
    }

    let counter = amount;
    Object.keys(this.props.crossedNumbers).forEach((coupon) => {
      const _crossedNumbers = this.props.crossedNumbers[coupon];
      if (
        counter > 0 &&
        this.props.couponGroup &&
        !this.props.couponGroup.numberGroups.every(
          (group, i) => _crossedNumbers[i].length >= group.minCrossed
        )
      ) {
        counter--;
        updateCouponNumbers(+coupon, this.props, (group, i) => {
          const min = group.startValue;
          const max = group.amountNumbers;
          const count = amountCouponNumbers
            ? amountCouponNumbers
            : group.maxCrossed;
          const size = count - _crossedNumbers[i].length;
          const unique = true;
          const exclude = _crossedNumbers[i];

          return exclude.concat(
            getRandomNumberArray(size, min, max, unique, exclude)
          );
        });
      }
    });
  };

  handleOnReceiveGameQuickTipData = (data: { [key: number]: number[] }) => {
    Object.keys(data).forEach((couponId: string) => {
      updateCouponNumbers(+couponId, this.props, data[couponId]);
    });
  };

  handleFinishStep = (): void => {
    if (
      typeof this.couponProgress !== 'undefined' &&
      this.couponProgress > 0 &&
      this.couponProgress < 100
    ) {
      return Modal.open('ModalInvalidCouponsInfo');
    }
    // clearInvalidCoupons();
    pushDatalayer(
      `click_element_name`,
      `Button - Fertig getippt`,
      DataLayerEvent.UserClick
    );
    this.props.handleNextStep();
  };

  get isKenoExternal(): boolean {
    return (this.props.id === 3 && this.props.external) || false;
  }

  render() {
    const {
      couponGroup,
      currentCouponId,
      crossedNumbers,
      gamePrice,
      viewSize,
      info,
      selectedDrawRate,
      drawRate,
      subsidiary,
    } = this.props;

    const preselectedSubsidiary = subsidiary ? subsidiary.substring(0, 2) : '';
    const currentSub = findSubsidiaryById(preselectedSubsidiary);
    const currentSubsidiaryName = currentSub?.displayName;

    const countFilledCouponsNumber = (): number => {
      return couponGroup
        ? countFilledCoupons(crossedNumbers, couponGroup.numberGroups)
        : 0;
    };

    const clearInvalidCoupons = (): void => {
      Object.keys(this.props.crossedNumbers).forEach(
        (couponId) =>
          this.props.couponGroup &&
          calculateCouponProgress(
            this.props.couponGroup.numberGroups,
            this.props.crossedNumbers[couponId]
          ) < 100 &&
          this.handleOnCouponClear(+couponId)
      );
    };

    const filledCouponsNumber = countFilledCouponsNumber();

    const currentCouponNumbers =
      crossedNumbers && crossedNumbers[currentCouponId]
        ? crossedNumbers[currentCouponId][0].length
        : null;
    const drawType =
      !!this.props.drawTypes &&
      this.props.drawTypes.find((item) => item.value === currentCouponNumbers);

    const drawTypeValue: number | null = drawType ? drawType.value : null;

    const kenoStake = this.props.selectedDrawRate;
    const rows =
      (drawTypeValue &&
        kenoData[drawTypeValue] &&
        kenoData[drawTypeValue].map((row) => [
          row.winningClassName,
          numberToCurrency(+kenoStake * row.winningSum, '€', true),
        ])) ||
      [];

    const couponModalInfo = info && info.coupons;

    return (
      <div className="h-flex-gm h-flex-gm--cols">
        <div className="h-flex__col h-flex__col--6-10 h-spacer-larger-bottom">
          {couponModalInfo && (
            <Modal id="coupon-info">
              <ModalHeader quitAction={() => this.setFocusOnOpenerButton()}>
                <span>Schließen</span>
              </ModalHeader>
              <ModalContent>
                <h4>{couponModalInfo.title}</h4>
                <p
                  dangerouslySetInnerHTML={this.replaceCouponModalInfoSpaceholders(
                    couponModalInfo.desc,
                    currentSubsidiaryName,
                    couponGroup?.amountCoupons
                  )}
                />
              </ModalContent>
            </Modal>
          )}

          <div style={{ position: 'relative' }}>
            <h1 className="Step__title">
              {this.props.title}{' '}
              {currentSubsidiaryName && <span>in {currentSubsidiaryName}</span>}{' '}
              spielen
            </h1>
            {!currentCouponId && (
              <>
                <h2
                  className="Step__header h-flex--justify-start"
                  style={{ alignItems: 'center' }}
                >
                  <span>Ihre Spielfelder</span>
                  {couponModalInfo && (
                    <Button
                      id={'cuponModalOpener'}
                      ariaLabel="Weitere Informationen"
                      onClick={() => Modal.open('coupon-info')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          Modal.open('coupon-info');
                          setTimeout(this.setFocusOnModalContent, 100);
                        }
                      }}
                    >
                      <Icon variant={IconVariant.Info} />
                    </Button>
                  )}
                </h2>
                {(!!currentCouponId || (!currentCouponId && couponGroup)) && (
                  <div className="h-flex-gm--justify-center-down h-spacer-large-bottom GameForm__subsidiary">
                    <label
                      htmlFor="currentSubsidiary"
                      className="GameForm__subsidiary-label"
                    >
                      <small
                        style={{
                          marginBottom: '10px',
                          display: 'block',
                        }}
                      >
                        Je nach Bundesland stehen optional zusätzliche
                        Spielfelder zur Verfügung:
                      </small>
                    </label>
                    <div className="GameForm__selectWrapper GameForm__selectWrapper-small">
                      <select
                        id="currentSubsidiary"
                        value={preselectedSubsidiary}
                        required={true}
                        className="GameForm__subsidiary-select"
                        onChange={(event) => {
                          this.handleSubsidiaryChange(
                            event.target.value,
                            couponGroup
                          );
                        }}
                      >
                        <option key={'none'} value="">
                          {preselectedSubsidiary
                            ? 'Meine Auswahl entfernen'
                            : 'Mein Bundesland wählen'}
                        </option>
                        {subsidiaryConfig
                          .sort((a, b) =>
                            a.displayName.localeCompare(b.displayName)
                          )
                          .map((mapSub) => (
                            <option key={mapSub.id} value={mapSub.id}>
                              {mapSub.displayName}
                            </option>
                          ))}
                      </select>
                      <Icon
                        variant={IconVariant.ChevronDown}
                        className="select-icon"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {!!currentCouponId && couponGroup && (
              <h2 className="Step__header">
                <span
                  className={classnames({
                    'h-hide-gm--down': !!this.props.drawTypes,
                  })}
                >
                  <span>Ihr Spielfeld</span>

                  <small className="h-hide-gm">
                    <br />
                    {`${currentCouponId} von ${couponGroup?.amountCoupons}`}
                  </small>
                </span>
                {this.props.drawTypes && (
                  <span className="h-hide-gm">
                    <span>KENO-Typ {drawTypeValue || '?'}</span>
                    <Button
                      onClick={() => Modal.open('keno-type-info')}
                      iconPosition={Button.IconPosition.Right}
                    >
                      <Icon variant={IconVariant.Info} />
                    </Button>
                  </span>
                )}
                <span>
                  {couponGroup?.amountCoupons > currentCouponId && (
                    <Button
                      className="h-hide-gm"
                      variant={Button.Variant.Primary}
                      disabled={!this.couponIsFilled}
                      fullWidth={true}
                      progress={this.couponProgress}
                      onClick={this.handleOnForwardClick}
                    >
                      <span>Weiter</span>
                      <Icon variant={IconVariant.ArrowRight} />
                    </Button>
                  )}

                  <small className="h-hide-gm--down">
                    {`${currentCouponId} von ${couponGroup?.amountCoupons}`}
                  </small>
                </span>
              </h2>
            )}
            {couponGroup && currentCouponId === 0 && (
              <StepTransition
                variant={TransitionVariant.Fade}
                onEnter={clearInvalidCoupons}
              >
                <CouponGroup
                  currentAmount={couponGroup.amountCoupons}
                  {...(couponGroup || {})}
                  onClickCoupon={this.handleOnCouponClick}
                  onClear={this.handleOnCouponClear}
                  onClearAll={this.handleOnCouponClearAll}
                  crossedNumbers={crossedNumbers}
                  viewSize={viewSize}
                />
              </StepTransition>
            )}
            {couponGroup && currentCouponId !== 0 && this.props.couponGroup && (
              <StepTransition variant={TransitionVariant.Zoom}>
                <>
                  {createArrayFromNumber(couponGroup?.maxAmountCoupons).map(
                    (couponIndex) => {
                      return (
                        couponIndex === currentCouponId && (
                          <StepTransition
                            variant={TransitionVariant.Zoom}
                            key={couponIndex}
                          >
                            <Coupon
                              key={currentCouponId}
                              index={currentCouponId}
                              showActionButtons={true}
                              columns={1}
                              numberGroups={couponGroup!.numberGroups}
                              quickTipPerCoupon={couponGroup!.quickTipPerCoupon}
                              quickTips={couponGroup!.quickTips}
                              onClickCouponNumber={
                                this.handleOnCouponNumberClick
                              }
                              onClear={this.handleOnCouponClear}
                              crossedNumbers={crossedNumbers[currentCouponId]}
                              viewSize={viewSize}
                              layout={couponGroup.layout}
                            />
                          </StepTransition>
                        )
                      );
                    }
                  )}
                </>
              </StepTransition>
            )}
            {!currentCouponId && (
              <div className="Coupon__actionButtons">
                <Button
                  onClick={() => {
                    this.handleOnCouponClearAll();
                    pushDatalayer(
                      `click_element_name`,
                      `Alle Felder löschen`,
                      DataLayerEvent.UserClick
                    );
                  }}
                  disabled={filledCouponsNumber === 0}
                >
                  <Icon variant={IconVariant.Trash} />
                  <span>Alle Felder löschen</span>
                </Button>

                {!!filledCouponsNumber &&
                  couponGroup &&
                  filledCouponsNumber < couponGroup?.amountCoupons && (
                    <QuickTips>
                      <Button
                        className="h-hide-gm"
                        variant={Button.Variant.QuickTip}
                        onClick={() => {
                          this.setState(() => ({
                            currentQuickTip: QuickTipType.Random,
                          }));

                          pushDatalayer(
                            `click_element_name`,
                            `Quick-Tipp - ${QuickTipType.Random}`,
                            DataLayerEvent.UserClick
                          );

                          if (!currentCouponId && !!couponGroup) {
                            Modal.open(
                              this.props.drawTypes
                                ? 'ModalQuickTipDrawTypes'
                                : 'ModalQuickTipCouponAmount'
                            );
                          } else {
                            if (!!this.props.drawTypes) {
                              Modal.open('ModalQuickTipDrawTypes');
                            } else {
                              this.handleOnCouponFill(currentCouponId, undefined);
                            }
                          }
                        }}
                      >
                        <Icon variant={IconVariant.Random} />
                        <span>Zufall</span>
                      </Button>
                    </QuickTips>
                  )}
              </div>
            )}
            {!!currentCouponId && (
              <div className="Coupon__actionButtons">
                <Button
                  onClick={() => {
                    this.handleOnCouponClear(currentCouponId);
                    pushDatalayer(
                      `click_element_name`,
                      `Löschen`,
                      DataLayerEvent.UserClick
                    );
                  }}
                  disabled={this.couponProgress === 0}
                >
                  <Icon variant={IconVariant.Trash} />
                  <span>Löschen</span>
                </Button>
                <div className="h-hide-gm">
                  <QuickTips>
                    <Button
                      variant={Button.Variant.QuickTip}
                      onClick={() => {
                        this.setState(() => ({
                          currentQuickTip: QuickTipType.Random,
                        }));

                        pushDatalayer(
                          `click_element_name`,
                          `Quick-Tipp - ${QuickTipType.Random}`,
                          DataLayerEvent.UserClick
                        );

                        if (!currentCouponId && !!couponGroup) {
                          Modal.open(
                            this.props.drawTypes
                              ? 'ModalQuickTipDrawTypes'
                              : 'ModalQuickTipCouponAmount'
                          );
                        } else {
                          if (!!this.props.drawTypes) {
                            Modal.open('ModalQuickTipDrawTypes');
                          } else {
                            this.handleOnCouponFill(currentCouponId, undefined);
                          }
                        }
                      }}
                    >
                      <Icon variant={IconVariant.Random} />
                      <span>Zufall</span>
                    </Button>
                  </QuickTips>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="h-flex__col h-flex__col--4-10">
          <div
            className={classnames({
              'h-hide h-show-gm': filledCouponsNumber > 0 || !!currentCouponId,
            })}
          >
            {(!!currentCouponId ||
              (!currentCouponId &&
                couponGroup &&
                filledCouponsNumber < couponGroup?.amountCoupons)) && (
              <>
                <div className="h-flex-gm--justify-center-down h-spacer-large-bottom">
                  <h3 className="Step__header">Quick-Tipps</h3>
                  <small
                    style={{
                      marginBottom: '10px',
                      display: 'block',
                    }}
                  >
                    Falls Sie Vorschläge für Zahlen per Quick-Tipp generieren
                    möchten, stellen wir Ihnen die folgende Auswahlhilfe zur
                    Verfügung. Die Gewinnwahrscheinlichkeit ändert sich durch
                    Ihre Auswahl nicht.
                  </small>

                  <QuickTips>
                    <Button
                      variant={Button.Variant.QuickTip}
                      onClick={() => {
                        this.setState(() => ({
                          currentQuickTip: QuickTipType.Random,
                        }));

                        pushDatalayer(
                          `click_element_name`,
                          `Quick-Tipp - ${QuickTipType.Random}`,
                          DataLayerEvent.UserClick
                        );

                        if (!currentCouponId && !!couponGroup) {
                          Modal.open(
                            this.props.drawTypes
                              ? 'ModalQuickTipDrawTypes'
                              : 'ModalQuickTipCouponAmount'
                          );
                        } else {
                          if (!!this.props.drawTypes) {
                            Modal.open('ModalQuickTipDrawTypes');
                          } else {
                            this.handleOnCouponFill(currentCouponId, undefined);
                          }
                        }
                      }}
                    >
                      <Icon variant={IconVariant.Random} />
                      <span>Zufall</span>
                    </Button>
                  </QuickTips>
                </div>

                {drawRate && !this.props.currentCouponId && (
                  <div className="h-spacer-large-bottom">
                    <h3 className="Step__header">Einsatz pro Spielfeld</h3>
                    <WeeksNumberSelection
                      options={drawRate}
                      label="Einsatz pro Spielfeld in Euro"
                      selectedValue={selectedDrawRate}
                      onChange={(value) =>
                        this.props.onDrawRateUpdate(this.props.id, value)
                      }
                    />
                  </div>
                )}

                <div className="h-spacer-medium-bottom" />

                {!!currentCouponId && this.props.drawTypes && (
                  <GameKenoTypeInfo
                    selectedDrawRate={this.props.selectedDrawRate}
                    kenoType={drawTypeValue}
                  />
                )}
                <div className="h-spacer-huge-bottom" />
              </>
            )}
          </div>

          {!!couponGroup &&
            !!currentCouponId &&
            couponGroup.amountCoupons > currentCouponId && (
              <p className="h-spacer-medium-bottom h-hide-gm--down">
                <Button
                  variant={Button.Variant.Primary}
                  disabled={!this.couponIsFilled}
                  fullWidth={true}
                  progress={this.couponProgress}
                  onClick={this.handleOnForwardClick}
                >
                  <span>Weiter zum {currentCouponId + 1}. Feld</span>
                  <Icon variant={IconVariant.ArrowRight} />
                </Button>
              </p>
            )}

          {filledCouponsNumber > 0 && (
            <>
              <p className="h-spacer-medium-bottom">
                <Button
                  variant={
                    !!currentCouponId
                      ? Button.Variant.Secondary
                      : Button.Variant.Primary
                  }
                  fullWidth={true}
                  onClick={() => this.handleFinishStep()}
                >
                  <Icon variant={IconVariant.Check} />
                  <span>Fertig getippt</span>
                </Button>
              </p>
              <SummaryPrice
                title="Ihr Spieleinsatz:"
                subtitle="zzgl. Bearbeitungsgebühr"
                price={gamePrice}
              />
              {MarketingParams.isCashback() && (
                <div>
                  <div
                    className={classnames(
                      'Cashback--tile',
                      `Cashback--tile-${MarketingParams.getNetwork()}`
                    )}
                  >
                    <div>
                      <div>
                        <img
                          src={MarketingParams.getNetworkLogo('game-module')}
                          alt=""
                        />
                        <div className={classnames('Cashback--tile-text')}>
                          Beim Kauf sammeln Sie{' '}
                          <b>{MarketingParams.getRewardCurrency()}</b>. Die
                          Abgabe des Spielauftrags erfolgt über Ihre zuständige
                          Landeslotteriegesellschaft.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {this.props.couponGroup && this.props.couponGroup.quickTips && (
          <ModalQuickTipsCouponAmount
            id="ModalQuickTipCouponAmount"
            withPlus={true}
            type={this.state.currentQuickTip}
            iconVariant={this.currentQuickTipIconVariant}
            quickTips={this.props.couponGroup.quickTips}
            onClickButton={(amountCouponToFill) => {
              this.handleOnCouponFillAll(
                amountCouponToFill,
                this.currentMaxCrossedAmount
              );
            }}
            amountCoupons={couponGroup ? couponGroup.amountCoupons : 0}
            filledCouponsNumber={filledCouponsNumber}
          />
        )}
        <Modal id={'quickTipsModalId'}>
          <ModalHeader>
            <Icon variant={IconVariant.QuickTips} />
            <span>Quick-Tipps</span>
          </ModalHeader>

          <ModalContent>
            <small
              style={{
                marginBottom: '10px',
                display: 'block',
              }}
            >
              Falls Sie Vorschläge für Zahlen per Quick-Tipp generieren möchten,
              stellen wir Ihnen die folgende Auswahlhilfe zur Verfügung. Die
              Gewinnwahrscheinlichkeit ändert sich durch Ihre Auswahl nicht.
            </small>
            <QuickTips>
              <Button
                variant={Button.Variant.QuickTip}
                onClick={() => {
                  Modal.close('quickTipsModalId');

                  pushDatalayer(
                    `click_element_name`,
                    `Quick-Tipp - ${QuickTipType.Random}`,
                    DataLayerEvent.UserClick
                  );

                  if (!currentCouponId && !!couponGroup) {
                    Modal.open(
                      this.props.drawTypes
                        ? 'ModalQuickTipDrawTypes'
                        : 'ModalQuickTipCouponAmount'
                    );
                  } else {
                    if (!!this.props.drawTypes) {
                      Modal.open('ModalQuickTipDrawTypes');
                    } else {
                      this.handleOnCouponFill(currentCouponId, undefined);
                    }
                  }
                }}
              >
                <Icon variant={IconVariant.Random} />
                <span>Zufall</span>
              </Button>
            </QuickTips>
          </ModalContent>
        </Modal>
        <Modal id="keno-type-info">
          <ModalHeader>
            <span>KENO-Typen</span>
          </ModalHeader>

          <ModalContent>
            <h4>Ihre Gewinnchancen</h4>
            <p>
              Der Gewinn richtet sich nach der Höhe Ihres Einsatzes. Diesen
              können Sie in einem späteren Schritt für Ihren gesamten
              Spielschein festlegen. Je nach Wunsch können Sie 1 Euro, 2 Euro, 5
              Euro oder 10 Euro einsetzen. Somit entscheiden Sie bei KENO über
              die Anzahl der getippten Zahlen, den Einsatz und die Laufzeit.
            </p>

            <h4>Bei Einsatz</h4>
            {this.props.drawRate && (
              <WeeksNumberSelection
                options={this.props.drawRate}
                label="Einsatz pro Spielfeld in Euro"
                selectedValue={this.props.selectedDrawRate}
                onChange={(value) =>
                  this.props.onDrawRateUpdate(this.props.id, value)
                }
              />
            )}

            {rows && rows.length > 0 && (
              <>
                <div className="h-spacer-large-bottom" />
                <Table alignColumns={['left', 'right']}>
                  <Table.Head rowsHead={['Anzahl Richtige', 'Gewinn']} />
                  <Table.Body rows={rows} />
                </Table>
              </>
            )}
          </ModalContent>
        </Modal>
        {this.props.drawTypes && (
          <ModalQuickTipDrawTypes
            id={'ModalQuickTipDrawTypes'}
            drawTypes={this.props.drawTypes}
            headerIconVariant={this.currentQuickTipIconVariant}
            onClickButton={(amount: number) => {
              this.currentMaxCrossedAmount = amount;

              if (!!currentCouponId) {
                this.handleOnCouponFill(currentCouponId, amount);
              } else {
                Modal.open('ModalQuickTipCouponAmount');
              }
            }}
          />
        )}
      </div>
    );
  }

  private couponIsFilled() {
    return !!this.couponProgress;
  }

  private get couponProgress(): number | undefined {
    return (
      this.props.couponGroup &&
      calculateCouponProgress(
        this.props.couponGroup.numberGroups,
        this.props.crossedNumbers[this.props.currentCouponId]
      )
    );
  }

  get currentQuickTipIconVariant(): IconVariant {
    return IconVariant[this.state.currentQuickTip.toString()];
  }
}

export default GameStepCoupons;
